import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Doc } from '@/convex/_generated/dataModel';
import type { GlobalSearchTask } from './types';
import { SearchContext } from './types';

interface SearchProviderProps {
  children: React.ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Fetch all user contexts
  const userContextsQuery = useQuery(api.contexts.getUserContexts);
  const userContexts = useMemo(() => userContextsQuery ?? [], [userContextsQuery]);

  // For now, let's use the current context approach until we create proper global queries
  // This is a simpler implementation that doesn't violate hooks rules
  const currentContextId = userContexts[0]?._id;
  
  // Fetch data only from the first/current context as a starting point
  const currentTasksQuery = useQuery(
    api.normalTasks.queries.getAllActiveTasks, 
    currentContextId ? { contextId: currentContextId } : 'skip'
  );
  const currentTasks = useMemo(() => currentTasksQuery ?? [], [currentTasksQuery]);
  
  const currentProjectsQuery = useQuery(
    api.projects.crud.list, 
    currentContextId ? { contextId: currentContextId } : 'skip'
  );
  const currentProjects = useMemo(() => currentProjectsQuery ?? [], [currentProjectsQuery]);
  
  const currentCategoriesQuery = useQuery(
    api.categories.list, 
    currentContextId ? { contextId: currentContextId } : 'skip'
  );
  const currentCategories = useMemo(() => currentCategoriesQuery ?? [], [currentCategoriesQuery]);

  // Aggregate and process search data
  const searchResults = useMemo(() => {
    const activeTasks: GlobalSearchTask[] = [];
    const completedTasks: GlobalSearchTask[] = [];
    const projects: (Doc<'projects'> & { contextName: string })[] = [];
    const categories: (Doc<'categories'> & { contextName: string })[] = [];

    // Process tasks from current context
    if (currentTasks && userContexts[0]) {
      const context = userContexts[0];
      
      currentTasks.forEach(task => {
        const project = currentProjects.find(p => p._id === task.projectId);
        const category = currentCategories.find(c => c._id === task.categoryId);

        const enrichedTask: GlobalSearchTask = {
          ...task,
          contextName: context.name,
          contextColor: context.color,
          projectName: project?.name,
          categoryName: category?.name,
        };

        if (task.completed) {
          completedTasks.push(enrichedTask);
        } else {
          activeTasks.push(enrichedTask);
        }
      });
    }

    // Process projects from current context
    if (currentProjects && userContexts[0]) {
      const context = userContexts[0];
      
      currentProjects.forEach(project => {
        projects.push({
          ...project,
          contextName: context.name,
        });
      });
    }

    // Process categories from current context
    if (currentCategories && userContexts[0]) {
      const context = userContexts[0];
      
      currentCategories.forEach(category => {
        categories.push({
          ...category,
          contextName: context.name,
        });
      });
    }

    return {
      activeTasks,
      completedTasks,
      projects,
      categories,
      contexts: userContexts,
    };
  }, [currentTasks, currentProjects, currentCategories, userContexts]);

  const openSearch = useCallback(() => setIsOpen(true), []);
  const closeSearch = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const value = useMemo(() => ({
    isOpen,
    search,
    setSearch,
    openSearch,
    closeSearch,
    searchResults,
  }), [isOpen, search, openSearch, closeSearch, searchResults]);

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};